import { Box, Button, Container, Stack, Typography } from '@mui/material';
import Bowser from 'bowser';
import { PropsWithChildren, useEffect, useState } from 'react';

// Mapping from bowser names to browserslist names
const BROWSER_MAP: Record<string, string> = {
  Chrome: 'chrome',
  Firefox: 'firefox',
  Safari: 'safari',
  'Microsoft Edge': 'edge',
  Opera: 'opera',
  'Samsung Internet for Android': 'samsung',
  'Android Browser': 'android',
  'Opera Mini': 'op_mini',
  'UC Browser': 'and_uc',
  'QQ Browser': 'and_qq',
};

export function BrowserGuard({ children }: PropsWithChildren) {
  const [isSupported, setIsSupported] = useState(true);
  const [browserName, setBrowserName] = useState('');
  const [browserVersion, setBrowserVersion] = useState('');

  useEffect(() => {
    const minBrowsers = import.meta.env.VITE_MIN_BROWSERS;
    // If no constraints defined, allow all
    if (!minBrowsers || Object.keys(minBrowsers).length === 0) {
      return;
    }

    const browser = Bowser.getParser(window.navigator.userAgent);
    const name = browser.getBrowserName();
    const version = browser.getBrowserVersion() || '';
    const osName = browser.getOSName();
    const platformType = browser.getPlatformType();

    setBrowserName(name);
    setBrowserVersion(version);
    const majorVersion = parseInt(version.split('.')[0], 10);

    let configKey = BROWSER_MAP[name];

    // Special handling for iOS Safari
    if (name === 'Safari' && (osName === 'iOS' || platformType === 'mobile' || platformType === 'tablet')) {
      configKey = 'ios_saf';
    }

    // Special handling for Chrome on Android
    if (name === 'Chrome' && osName === 'Android') {
      // browserslist usually returns 'and_chr' for Chrome on Android
      // But if 'and_chr' is not in our list, we can fall back to checking 'chrome'
      if (minBrowsers['and_chr']) {
        configKey = 'and_chr';
      }
    }

    // Special handling for Firefox on Android
    if (name === 'Firefox' && osName === 'Android') {
      if (minBrowsers['and_ff']) {
        configKey = 'and_ff';
      }
    }

    // Special handling for Opera on Mobile
    if (name === 'Opera' && (platformType === 'mobile' || platformType === 'tablet')) {
      if (minBrowsers['op_mob']) {
        configKey = 'op_mob';
      }
    }

    // Strict check: Must be in the map AND meet version requirement
    // If the browser is recognized (configKey exists) check against minBrowsers
    if (configKey && typeof minBrowsers[configKey] !== 'undefined') {
      const minVersion = minBrowsers[configKey];
      // minVersion 0 means "all" or no strict version limit
      if (minVersion > 0 && majorVersion < minVersion) {
        setIsSupported(false);
      }
    } else {
      // Browser not in our supported list or not recognized
      // If configKey is defined but not in minBrowsers, it means browserslist didn't include it
      // which implies it's not supported by the query (e.g. it is "dead" or < 1%)
      setIsSupported(false);
    }
  }, []);

  if (isSupported) {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        bgcolor: 'background.default',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Container maxWidth='sm'>
        <Stack spacing={3} alignItems='center' textAlign='center'>
          <Box component='img' src='/foresight-logo.svg' alt='Logo' sx={{ height: 40, mb: 2 }} />
          <Typography variant='h4' component='h1' gutterBottom>
            Browser Not Supported
          </Typography>
          <Typography variant='body1' color='text.secondary'>
            You are using {browserName} {browserVersion}. This application requires a modern browser. Please
            upgrade your browser or switch to Google Chrome.
          </Typography>
          <Button
            variant='contained'
            size='large'
            href='https://www.google.com/chrome/'
            target='_blank'
            rel='noopener noreferrer'
          >
            Download Chrome
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}
